import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { UsersTableColumns } from 'widgets/admin/Users/UsersTable/consts/UserTableColumns';
import { UserForAdmin } from 'entities/admin/Users/types';
import { removeUser } from 'entities/admin/Users/api';
import { RemoveUserModal } from 'widgets/admin/Users/RemoveUserModal';
import { GetUsersReq } from 'entities/admin/Users/api/types';

type Props = {
	users: Array<UserForAdmin>;
	filters: GetUsersReq;
	setFilters: (data: GetUsersReq) => void;
};

const UserTable: FC<Props> = ({ users, filters, setFilters }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [userForRemove, setUserForRemove] = useState<UserForAdmin | undefined>(undefined);

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationKey: ['admin/users', userForRemove?._id],
		mutationFn: removeUser,
	});

	const handleOpenModal = (user: UserForAdmin) => setUserForRemove(user);
	const handleCloseModal = () => setUserForRemove(undefined);

	const preparedUsers = useMemo(() => {
		if (!users.length) return [];
		return users.map((user) => ({
			...user,
			sort: filters.sort,
			openModal: (user: UserForAdmin) => {
				handleOpenModal(user);
			},
			navigateToDetails: (user: UserForAdmin) => {
				navigate(`/users/${user._id}`);
			},
			sortFunction: (sort: 'asc' | 'desc') => {
				setFilters({ ...filters, sort });
			},
			onRowChange: () => navigate(`/users/${user._id}`),
		}));
	}, [users]);

	const handleRemoveCoUser = useCallback(() => {
		mutate(userForRemove?._id, {
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
				enqueueSnackbar({
					message: t('admin.users.snackbar.success_user_remove', { value: userForRemove?.email }),
					variant: 'success',
				});
				handleCloseModal();
				navigate('/users');
			},
			onError: (err) => {
				showErrorNotification(
					err as AxiosError,
					t('admin.users.snackbar.error_user_remove', {
						value: userForRemove?.email,
					}),
				);
			},
		});
	}, [userForRemove, queryClient]);

	return (
		<>
			<RemoveUserModal
				open={!!userForRemove}
				email={userForRemove?.email}
				handleClose={handleCloseModal}
				handleRemoveUser={handleRemoveCoUser}
			/>
			{users?.length ? (
				<TableWithHeader
					columns={UsersTableColumns}
					rows={preparedUsers.map((user) => ({ ...user, id: user._id, user }))}
				/>
			) : (
				<EmptyTable text={t('admin.users.no_users_yet')} />
			)}
		</>
	);
};

export default UserTable;
